/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import {
  PagedIApiContentResponseModel,
  IApiContentResponseModel,
} from "./DeliveryApiClient";

type TItem = {
  content: {
    contentType: string;
    properties: {
      value: any;
      propertyKey: string;
      backofficeDisplayName?: string;
    };
  };
};

type TCmsEdit = {
  backofficeDisplayName: string;
  itemId: string;
  editLink: string;
};

export type TEnv = "prod" | "uat" | "ci" | "dev";
export type TLanguage = "da" | "en";

const logError = (env: TEnv, id: string, message?: string) => {
  if (env !== "prod") {
    // eslint-disable-next-line no-console
    console.error({
      id,
      message:
        message ||
        `We made an assumption of a data structure in the dictionary API. This is an error. 
      Contact Team Hverve & Synlighed so this can be fixed`,
    });
  }
};

const logEmptyValue = (env: TEnv, id: string, message?: string) => {
  if (env !== "prod") {
    // eslint-disable-next-line no-console
    console.warn({
      id,
      message:
        message || "there is an empty value in Umbraco. Is this on purpose?",
    });
  }
};

const dataIsTruthy = (data: any, type?: string) => {
  if (data === null || data === undefined) {
    return false;
  }
  if (type) {
    if (type === "array") {
      if (!Array.isArray(data)) {
        return false;
      }
      return true;
    }
    // eslint-disable-next-line valid-typeof
    if (typeof data !== type) {
      return false;
    }
  }
  return true;
};

const handleValue = (value: any, contentType: string): any => {
  switch (contentType) {
    case "miniCMS_linkBlock":
    case "miniCMS_imageBlock":
      if (dataIsTruthy(value, "array")) {
        return value[0];
      }
      return value;

    case "miniCMS_RTEBlock":
      if (dataIsTruthy(value, "object")) {
        return value.markup;
      }
      return value;

    default:
      return value;
  }
};

const handleDisplayName = (value: any, contentType: string): string => {
  switch (contentType) {
    case "miniCMS_RTEBlock":
      return "Rich Text";
    default:
      return value;
  }
};

const createEditLink = (
  itemId: string,
  env: TEnv,
  language: TLanguage
): string => {
  const hostnames: Record<TEnv, string> = {
    prod: "umbraco.ida.dk",
    uat: "umbraco.uat.ida.dk",
    ci: "umbraco.ci.ida.dk",
    dev: "umbraco.dev.ida.dk",
  };
  const hostname = hostnames[env];

  if (!dataIsTruthy(hostname, "string")) {
    logError(env, "hostname");
    return "";
  }

  return `https://${hostname}/umbraco#/content/content/edit/${itemId}?mculture=${language}`;
};

const createItemResult = (
  contentItem: TItem,
  showCmsEdit: boolean,
  itemId: string,
  env: TEnv,
  language: TLanguage,
  parentDisplayName?: string
): { cmsEdit?: TCmsEdit; value: any } => {
  const value = contentItem?.content?.properties?.value;
  const backofficeDisplayName =
    contentItem?.content?.properties?.backofficeDisplayName;
  const contentType = contentItem?.content?.contentType;

  if (!dataIsTruthy(contentType, "string")) {
    logError(env, "contentType");
  }

  const prefix = parentDisplayName ? `${parentDisplayName}/` : "";
  const displayName =
    backofficeDisplayName || handleDisplayName(value, contentType);

  const result = {
    value: handleValue(value, contentType),
    cmsEdit: showCmsEdit
      ? {
          backofficeDisplayName: `${prefix}${displayName}`,
          itemId,
          editLink: createEditLink(itemId, env, language),
        }
      : undefined,
  };

  return result;
};

const createNestedItemResult = (
  nestedItems: TItem[],
  showCmsEdit: boolean,
  itemId: string,
  backofficeDisplayName: string,
  env: TEnv,
  language: TLanguage
): { [key: string]: any } => {
  const nestedItemResult: { [key: string]: any } = {};

  nestedItems?.forEach((nestedItem: TItem) => {
    if (!dataIsTruthy(nestedItem?.content?.properties)) {
      logError(env, "nested-item");
      return;
    }

    const { propertyKey } = nestedItem.content.properties;

    if (!dataIsTruthy(propertyKey, "string")) {
      logError(env, "property-key-nested");
      return;
    }

    nestedItemResult[propertyKey] = createItemResult(
      nestedItem,
      showCmsEdit,
      itemId,
      env,
      language,
      backofficeDisplayName
    );
  });

  nestedItemResult.cmsEdit = showCmsEdit
    ? {
        backofficeDisplayName,
        itemId,
        editLink: createEditLink(itemId, env, language),
      }
    : undefined;
  return nestedItemResult;
};

const groupByKey = (objects: TItem[], env: TEnv): Record<string, TItem[]> => {
  try {
    return objects.reduce((acc, obj) => {
      const propertyKey = obj?.content.properties?.propertyKey;
      if (propertyKey) {
        (acc[propertyKey as keyof typeof acc] as TItem[]) = acc[
          propertyKey as keyof typeof acc
        ]
          ? [...acc[propertyKey as keyof typeof acc], obj]
          : [obj];
      }
      return acc;
    }, {});
  } catch {
    logError(env, "group-by-key");
    return {};
  }
};

const mapItem = (
  group: TItem,
  showCmsEdit: boolean,
  itemId: string,
  env: TEnv,
  language: TLanguage
): { cmsEdit?: TCmsEdit; [key: string]: any } => {
  const value = group?.content?.properties?.value;
  const backofficeDisplayName =
    group?.content?.properties?.backofficeDisplayName;

  if (!dataIsTruthy(value)) {
    logEmptyValue(env, "value");
  }

  if (!dataIsTruthy(backofficeDisplayName, "string")) {
    logEmptyValue(
      env,
      "backoffice-display-name",
      `A Backoffice Display Name for a field in Umbraco is missing. You should consider updating this`
    );
  }

  if (typeof value === "object" && value?.items) {
    return createNestedItemResult(
      value.items as TItem[],
      showCmsEdit,
      itemId,
      backofficeDisplayName || value,
      env,
      language
    );
  }

  return createItemResult(group, showCmsEdit, itemId, env, language, undefined);
};

export const ConvertToDictionary = (
  data: PagedIApiContentResponseModel,
  showCmsEdit = false,
  env: TEnv = "ci",
  language: TLanguage = "da"
): Record<
  string,
  Record<string, { cmsEdit?: TCmsEdit; [key: string]: any }>
> => {
  const result: Record<
    string,
    Record<string, { cmsEdit?: TCmsEdit; [key: string]: any }>
  > = {};

  if (env !== "ci" && env !== "dev" && env !== "prod" && env !== "uat") {
    // We set to "ci" because if we set to prod (to make sure it's not emmitted in prod),
    // then we also won't get the message in the other environments
    env = "ci";
    logError(
      "ci",
      `An unknown environment has been passed to the converter. 
    Only 'prod', 'uat', 'ci', 'dev' is allowed. We have changed the environment to 'ci'`
    );
  }

  if (language !== "da" && language !== "en") {
    language = "da";
    logError(
      env,
      `An unknown language has been passed to the converter. 
    Only 'da' and 'en' are supported. We have changed the language to 'da'`
    );
  }

  if (!dataIsTruthy(data?.items)) {
    logError(env, "data-items");
    return result;
  }

  data.items.forEach((item: IApiContentResponseModel) => {
    const itemResult: Record<
      string,
      { cmsEdit?: TCmsEdit; [key: string]: any }
    > = {};

    if (!dataIsTruthy(item?.properties?.content)) {
      logError(env, "content");
      return;
    }

    const itemId = item?.id;
    const items = item?.properties?.content?.items;
    const propertyKey = item?.properties?.propertyKey;

    if (!dataIsTruthy(itemId, "string")) {
      logError(env, "item-id");
      return;
    }
    if (!dataIsTruthy(items, "array")) {
      logError(env, "content-items");
      return;
    }
    if (!dataIsTruthy(propertyKey, "string")) {
      logError(env, "property-key");
      return;
    }

    const groupedItems = groupByKey(items, env);

    Object.entries(groupedItems).forEach(([groupName, group]) => {
      group.forEach((groupItem, index) => {
        const subKey = groupItem?.content?.properties?.propertyKey;

        if (!dataIsTruthy(subKey, "string")) {
          logError(env, "sub-key");
          return;
        }

        const propKey = index === 0 ? subKey : `${subKey}_${index}`;
        const mappedItem = mapItem(
          groupItem,
          showCmsEdit,
          itemId!,
          env,
          language
        );

        itemResult[propKey] = mappedItem;
      });
    });

    result[propertyKey] = itemResult;
  });

  return result;
};
